// THE DEMO SHOP'S OWN CHROME (TW-0135) — the dials, and the sentence under them.
//
// Only demo.waivers.tattoo shows any of this. A real shop's form is its own shop's, set once in
// Settings; nothing here is reachable from a printed QR code on a counter.
//
// Chris, 2026-09-08, reviewing it: "i think the dropdown might suggest that we don't support states
// not listed. Any state people might tap and then hunt for their state but not see it, you know?"
// and "i feel like changing anything from the default should adjust the help text. like we can say
// what changes if a user selects t&p, just p, and if they check the We pierce minors you know?"
//
// Both are the same defect seen twice: the demo showed a state picker that answered ONE question
// (does this state's form differ?) and let a shop owner read a much bigger answer off it (do you
// support my state?). The picker lists all 51 now, and the line under it says what THIS
// configuration changes — including the case where the honest answer is "nothing".
import type { Lang, Strings } from "./i18n";
import { contractChangeWords, formChangesFor, STATE_NAMES, wordedQuestionChangeWords } from "./stateForms";
import {
  bothParentsPlace,
  countyRuleFor,
  earExemptionOf,
  minorsRuleFor,
  piercingMinorsRule,
  requiresBothParents,
  requiresBothParentsPresent,
  requiresGuardianProof,
  stateSetsNoRule,
  tattooMinorsRule,
} from "./minors";
import type { ShopType } from "./service";
import { cosmeticBarsFor, cosmeticRuleFor } from "./cosmetic";

/** What the demo shop does: a shop type, or (TW-0279, `?type=cosmetic`) a cosmetic studio. */
export type DemoType = ShopType | "cosmetic";

export type DemoDialState = {
  /** the state picked, or null for "any state" */
  state: string | null;
  /** what the demo shop does today */
  type: DemoType;
  /** whether the demo shop pierces minors with a parent present */
  minors: boolean;
  /** 2026-09-24 — the fourth dial: the birth certificate or guardianship papers when a parent signs (`?proof=1`) */
  proof?: boolean;
  /** TW-0227 — where in the state, off a link (`?county=Lake%20County&city=Hammond`): stateForms.ts demoPlaceFromUrl */
  county?: string | null;
  countyKind?: string | null;
  city?: string | null;
  /** TW-0225 — the fifth: the demo client is here for an ear piercing (`?ear=1`) */
  ear?: boolean;
};

/** The whole state list, alphabetical by name — ALL 51, not only the ones whose form differs.
 *
 *  It used to list the 9 states with their own questions, which reads as the list of states we
 *  serve. Every shop in the other 42 could scroll past their own and conclude we do not cover
 *  them, and a shop that concludes that does not sign up to find out. The signal the short list
 *  carried is not lost — it moved into the sentence under the picker, where it can say the useful
 *  thing rather than imply the harmful one. */
export function everyState(): { abbr: string; name: string }[] {
  return Object.entries(STATE_NAMES)
    .map(([abbr, name]) => ({ abbr, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** EVERY ID THE "ADDS" SENTENCE CAN CARRY, IN THE WORDS A SHOP OWNER READS. Exported so the test can hold it to the
 *  contract. On 2026-09-21 the live demo read "California adds: risk_disclosure_tattoo, aftercare_suggestions, an
 *  allergies question, …": ten ids had joined the contract since this map was written, the fallback printed the id
 *  itself, and 13 states' sentences carried one. Found while answering a California shop owner with a link to it. */
export function changeWords(T: Strings, lang: Lang = "en"): Record<string, string> {
  return {
    // TW-0526 (2026-10-28, the audit's M04) — the questions the contract carries in their rule's own words bring their demo
    // words with them (scripts/state-forms.py VOCAB `change`), so a state's next one never reaches the sentence as its id
    ...wordedQuestionChangeWords(lang),
    // TW-0526, the client-form side (2026-10-29) — and a box the client fills, or a notice whose words ride the contract, the same
    // way (VOCAB `change`): Oklahoma's hour, Mississippi's sex as on the ID, the parent's phone, Kentucky's attestation
    ...contractChangeWords(lang),
    allergies: T.changeAllergies,
    bleeding_disorders: T.changeBleeding,
    eaten_recently: T.changeEatenRecently,
    medications: T.changeMedications,
    lot_numbers: T.changeLots,
    artist_license_number: T.changeLicense,
    emergency_contact: T.changeEmergencyContact,
    blood_donor_notice: T.changeBloodDonor,
    grievance_procedure: T.changeGrievance,
    health_authority_contact: T.changeHealthAuthority,
    aftercare_suggestions: T.changeAftercareSuggestions,
    state_disclosure: T.changeStateDisclosure,
    risk_disclosure_tattoo: T.changeRiskTattoo,
    risk_disclosure_piercing: T.changeRiskPiercing,
    cautionary_notice: T.changeCautionaryNotice,
    consent_statements: T.changeConsentStatements,
    complications_information: T.changeComplicationsInformation,
    decline_statement: T.changeDeclineStatement,
    numbing_prescription: T.changeNumbingPrescription,
    // 2026-10-16 (TW-0270 slice 4) — the five a state's rule can list as its own questions
    diabetes: T.changeDiabetesHistory,
    seizures_or_fainting: T.changeSeizuresOrFainting,
    heart_or_blood_pressure: T.changeHeartOrBloodPressure,
    blood_thinners_recently: T.changeBloodThinnersRecently,
    pregnant_recently: T.changePregnantRecently,
    // 2026-10-28 (TW-0526, the audit's M03) — the statements a state's rule has the consent carry that are no risk list, and
    // the piercing's own aftercare sheet
    aftercare_suggestions_piercing: T.changeAftercareSuggestionsPiercing,
    procedure_explanation_tattoo: T.changeProcedureExplanationTattoo,
    procedure_explanation_piercing: T.changeProcedureExplanationPiercing,
    ink_regulatory_status: T.changeInkRegulatoryStatus,
    // 2026-10-28 (TW-0526, the audit's M12) — the box under the aftercare sheet: received, and understood
    aftercare_received: T.changeAftercareReceived,
  };
}

/** TW-0279 — what every cosmetic form asks whatever its state does (the proposal's B10, B4 and B3 reuse these), so a
 *  state that adds them changes nothing on a cosmetic studio's form and its sentence does not claim it does. And since
 *  2026-10-16 (TW-0270 slice 4) a state's diabetes question: every cosmetic form asks its own (cosmetic.ts), so the state's
 *  is asked on a tattoo and a piercing alone (Form.tsx askDiabetes). */
const ON_EVERY_COSMETIC_FORM = new Set(["allergies", "bleeding_disorders", "medications", "diabetes"]);

/** The services a demo shop's rule is read for: a cosmetic studio's signing follows the state's TATTOO rule for what the
 *  state adds to the form, as the contract says per state (`cosmetic[state].follows`), never by a code default. */
function servicesForChanges(type: DemoType, state: string | null): string[] {
  if (type === "both") return ["tattoo", "piercing"];
  if (type === "cosmetic") {
    const follows = cosmeticRuleFor(state)?.follows;
    return follows ? ["cosmetic", follows] : ["cosmetic"];
  }
  return [type];
}

/** A service's noun in the demo's sentences: each its own, never one standing in for another. */
const demoWork = (service: string, T: Strings): string =>
  service === "tattoo" ? T.demoWorkTattoo : service === "piercing" ? T.demoWorkPiercing : T.demoWorkCosmetic;

/** What this configuration changes about the form, one plain line each, in the language on screen.
 *  Empty when every dial is where it started — the plainest demo says nothing about itself. */
export function demoChanges(dials: DemoDialState, T: Strings, lang: Lang): string[] {
  const lines: string[] = [];
  const stateName = dials.state ? STATE_NAMES[dials.state] ?? dials.state : null;

  // ── the state ──────────────────────────────────────────────────────────────────────────────
  if (stateName) {
    // Only what the state adds for the work THIS demo shop does: California's tattoo risks and tattoo aftercare
    // suggestions are not changes to a piercing-only shop's form. And NEVER A RAW ID: an id with no words is left
    // out of the sentence, and demo.test.ts fails the day the contract carries one this map does not.
    const services = servicesForChanges(dials.type, dials.state);
    const words = changeWords(T, lang);
    const named = formChangesFor(dials.state, services)
      .filter((id) => dials.type !== "cosmetic" || !ON_EVERY_COSMETIC_FORM.has(id))
      .map((id) => words[id])
      .filter(Boolean);
    if (named.length) {
      lines.push(`${T.demoStateAdds(stateName)}: ${named.join(", ")}.`);
    } else {
      // THE LINE THAT FIXES THE PICKER. A state whose form is the standard one has to say so out
      // loud, or its absence from any list of "different" states reads as absence from the product.
      lines.push(T.demoStateStandard(stateName));
    }
    // TW-0279 — and a cosmetic procedure the state's rule keeps off the list, in the rule's own words (Georgia's eyeliner)
    if (dials.type === "cosmetic") {
      for (const bar of cosmeticBarsFor(dials.state, {})) lines.push(lang === "es" ? bar.refusal.es : bar.refusal.en);
    }
  }

  // ── what the shop does ─────────────────────────────────────────────────────────────────────
  if (dials.type === "both") lines.push(T.demoTypeBothMeans);
  if (dials.type === "piercing") lines.push(T.demoTypePiercingMeans);
  if (dials.type === "cosmetic") lines.push(T.demoTypeCosmeticMeans);

  // ── a minor, with a parent present ─────────────────────────────────────────────────────────
  // The shop's switch is only the FIRST of three gates and the other two belong to the state, so
  // turning it on where the state says no has to SAY so rather than look like it worked
  // (minors.ts holds both rules).
  //
  // ONE ANSWER PER SERVICE (TW-0150), because the two laws disagree: a "both" shop in Oregon gets
  // a yes for the piercing and a no for the tattoo, on two lines, which is the clearest thing this
  // demo can show a shop owner about why we ask the question twice.
  // TW-0279 — a cosmetic studio takes no minor in any state, whatever its switch says: one line, and none of the tattoo's
  // or the piercing's (each service is named on its own branch below, never by what it is not).
  if (dials.minors && dials.type === "cosmetic") {
    lines.push(T.demoMinorsCosmetic);
  } else if (dials.minors) {
    if (!stateName) {
      lines.push(T.demoMinorsPickState);
    } else {
      // 2026-11-03 — the third answer: a state that sets no rule for the service (Hawaii, Iowa, Vermont and Washington, for a
      // piercing), where the signed waiver prints the shop's own rule and never "what <state> requires"
      const asked: [string, boolean, boolean][] = [];
      if (dials.type === "tattoo" || dials.type === "both") {
        asked.push([T.demoWorkTattoo, Boolean(tattooMinorsRule(dials.state)?.allowed), stateSetsNoRule("tattoo", dials.state)]);
      }
      if (dials.type === "piercing" || dials.type === "both") {
        asked.push([T.demoWorkPiercing, Boolean(piercingMinorsRule(dials.state)?.allowed), stateSetsNoRule("piercing", dials.state)]);
      }
      asked.forEach(([work, allowed, noRule]) =>
        lines.push(
          !allowed
            ? T.demoMinorsRefused(stateName, work)
            : noRule
              ? T.demoMinorsShopRule(stateName, work)
              : T.demoMinorsAllowed(stateName, work)
        )
      );
      // 2026-09-24 — THE FOURTH DIAL: the photo of the birth certificate or guardianship papers. Ticked, the
      // line says the form asks; unticked where the STATE names the proof, the line says the state asks on
      // its own — that is the rule, not the box (minors.ts requiresGuardianProof, read for each service: the
      // piercing rule carries its own since TW-0224, 2026-09-25, so a piercing demo in Michigan says it too).
      const services = dials.type === "both" ? ["tattoo", "piercing"] : [dials.type];
      // TW-0227 — A PLACE WITH A RULE OF ITS OWN (a link's `?county=` and `?city=`): one line naming it, for every
      // service whose rule carries it, so a Hammond demo says Hammond's rule is being followed and not only Indiana's
      const place = { county: dials.county ?? null, kind: dials.countyKind ?? null, city: dials.city ?? null };
      const local = services
        .map((s) => ({ s, rule: minorsRuleFor(s, dials.state) }))
        .filter(({ rule }) => rule?.allowed)
        .map(({ s, rule }) => ({ s, found: countyRuleFor(rule, place.county, place.kind, place.city) }))
        .filter(({ found }) => Boolean(found));
      if (local.length) {
        const works = local.map(({ s }) => demoWork(s, T));
        lines.push(T.demoCountyRule(local[0].found!.label, stateName, works));
      }
      // TW-0227 step 3 — …AND WHERE THAT RULE WANTS BOTH PARENTS, what the parent who is there will be asked: three ways
      // in Hammond and East Chicago, two in Delaware County, where both have to be here. The form's own reads
      // (minors.ts requiresBothParents, requiresBothParentsPresent, bothParentsPlace), so the demo says what it does.
      const bothRule = services
        .map((s) => minorsRuleFor(s, dials.state))
        .find((rule) => rule?.allowed && requiresBothParents(rule, place.county, place.kind, place.city));
      if (bothRule) {
        const who = bothParentsPlace(bothRule, place.county, place.kind, place.city) || stateName;
        lines.push(
          requiresBothParentsPresent(bothRule, place.county, place.kind, place.city)
            ? T.demoBothParentsPresent(who)
            : T.demoBothParents(who)
        );
      }
      const byRule = services.filter((s) =>
        requiresGuardianProof(minorsRuleFor(s, dials.state), place.county, place.kind, place.city)
      );
      if (dials.proof) {
        lines.push(T.demoProofOn);
      } else if (byRule.length) {
        const works = byRule.map((s) => demoWork(s, T));
        lines.push(T.demoProofByRule(stateName, works));
      }
      // TW-0225 — THE FIFTH DIAL: a minor's ear piercing, and what the picked state's rule does with one. A carve-out asks
      // the one question, in the words the form asks it (minors.ts earExemptionOf); a state that pierces minors with no
      // carve-out asks of an ear what it asks of any spot; a state that refuses a minor's piercing already said so above.
      // A tattoo-only demo has no piercing to ask about.
      if (dials.ear && (dials.type === "piercing" || dials.type === "both")) {
        // 2026-10-08 — with the stud-gun switch on, as the dial sets it on the demo's row (App.tsx withDemo)
        const rule = minorsRuleFor("piercing", dials.state, { ask_stud_gun: true });
        const carveOut = earExemptionOf(rule);
        if (carveOut.scope !== "none") {
          lines.push(T.demoEarAsks(stateName, T.earAsk(carveOut.scope, `${carveOut.method ?? ""}`)));
        } else if (rule?.allowed) {
          lines.push(T.demoEarCovered(stateName));
        }
      }
    }
  }

  return lines;
}

